import { Injectable } from "@angular/core";
import { Pdv } from "../interfaces/pdv";

/**
 * Cache mémoire des listes de PDV par campagne (FR-170). Les PDV d'un
 * intervenant évoluent peu : on évite de recharger la liste à chaque ouverture
 * de la liste PDV (pattern `pdvs_needs_refresh` de l'appli client).
 *
 * Stratégie : cache-first par campagne (clé = campaignId + fullSet). Le cache
 * vit le temps de la session ; il est rafraîchissable explicitement
 * (pull-to-refresh) via `invalidate`, et vidé au logout via `clear`.
 *
 * Porte aussi l'ensemble des PDV déjà rattachés à une mission de la campagne
 * (FR-180) : la page campagne-horsfichier le renseigne au chargement (elle
 * dispose déjà des constats), pdv-select l'utilise pour afficher le flag
 * « Déjà ajouté », et missions-detail y ajoute le PDV qu'il vient de traiter.
 * Ce dernier point est indispensable depuis FR-183 : l'envoi d'un constat
 * renvoie désormais sur pdv-select sans repasser par campagne-horsfichier.
 */
@Injectable({
  providedIn: "root",
})
export class PdvCacheService {
  private cache = new Map<string, Pdv[]>();

  /** Clé : campaignId → accountid des PDV ayant déjà une mission (FR-180). */
  private pdvAvecMissionParCampagne = new Map<string, Set<string>>();

  /**
   * Requêtes en vol par clé (FR-179) : le préchargement au démarrage et
   * l'ouverture de pdv-select peuvent réclamer la même liste au même moment —
   * ils partagent alors une seule requête au lieu d'en émettre deux.
   */
  private enVol = new Map<string, Promise<Pdv[]>>();

  /**
   * Génération du cache : incrémentée par `clear()` (logout). Une requête en
   * vol qui se résout après coup n'écrit pas dans le cache du compte suivant.
   */
  private generation = 0;

  private key(campaignId: string, fullSet: boolean): string {
    return `${campaignId}|${fullSet ? "uga" : "mes"}`;
  }

  /**
   * Liste du cache si présente, sinon via `loader` — en partageant la requête
   * avec tout autre appelant de la même clé déjà en vol (FR-179).
   */
  async obtenir(
    campaignId: string,
    fullSet: boolean,
    loader: () => Promise<Pdv[]>
  ): Promise<Pdv[]> {
    const cle = this.key(campaignId, fullSet);
    const enCache = this.cache.get(cle);
    if (enCache) {
      return enCache;
    }
    let promesse = this.enVol.get(cle);
    if (!promesse) {
      const generation = this.generation;
      promesse = loader()
        .then((pdvs) => {
          const liste = pdvs ?? [];
          if (generation === this.generation) {
            this.cache.set(cle, liste);
          }
          return liste;
        })
        .finally(() => {
          // Ne retirer que SA propre inscription : après un logout, une
          // nouvelle requête a pu s'enregistrer sous la même clé — la
          // supprimer priverait les appelants suivants du partage en vol
          // (review Greptile PR #308).
          if (this.enVol.get(cle) === promesse) {
            this.enVol.delete(cle);
          }
        });
      this.enVol.set(cle, promesse);
    }
    return promesse;
  }

  get(campaignId: string, fullSet = false): Pdv[] | undefined {
    return this.cache.get(this.key(campaignId, fullSet));
  }

  has(campaignId: string, fullSet = false): boolean {
    return this.cache.has(this.key(campaignId, fullSet));
  }

  set(campaignId: string, pdvs: Pdv[], fullSet = false): void {
    this.cache.set(this.key(campaignId, fullSet), pdvs);
  }

  /**
   * Enregistre l'ensemble des PDV déjà rattachés à une mission de la campagne
   * (FR-180). Remplace l'ensemble connu : c'est la vue de référence, issue des
   * constats de la campagne.
   */
  setPdvAvecMission(campaignId: string, pdvIds: string[]): void {
    this.pdvAvecMissionParCampagne.set(
      campaignId,
      new Set(pdvIds.filter(Boolean))
    );
  }

  /**
   * Ajoute un PDV à l'ensemble sans rechargement (FR-180) : appelé juste après
   * la création réussie d'un constat hors fichier, pour que le flag soit exact
   * au retour immédiat sur pdv-select (FR-183).
   */
  marquerPdvAvecMission(campaignId: string, pdvId: string): void {
    if (!campaignId || !pdvId) {
      return;
    }
    const existant = this.pdvAvecMissionParCampagne.get(campaignId);
    if (existant) {
      existant.add(pdvId);
      return;
    }
    this.pdvAvecMissionParCampagne.set(campaignId, new Set([pdvId]));
  }

  /** PDV déjà rattachés à une mission de la campagne (vide si inconnu). */
  pdvAvecMission(campaignId: string): Set<string> {
    return this.pdvAvecMissionParCampagne.get(campaignId) ?? new Set<string>();
  }

  /** Invalide une campagne (les deux jeux) ou tout le cache si non précisé. */
  invalidate(campaignId?: string): void {
    if (!campaignId) {
      this.cache.clear();
      this.pdvAvecMissionParCampagne.clear();
      return;
    }
    this.cache.delete(this.key(campaignId, false));
    this.cache.delete(this.key(campaignId, true));
    this.pdvAvecMissionParCampagne.delete(campaignId);
  }

  clear(): void {
    // Logout : les requêtes encore en vol ne doivent pas repeupler le cache
    // du compte suivant (appareil partagé) — la génération les disqualifie.
    this.generation++;
    this.enVol.clear();
    this.cache.clear();
    this.pdvAvecMissionParCampagne.clear();
  }
}
